import {
  User,
  MapPin,
  Calendar,
  Compass,
  Trophy,
  Users,
  ShoppingBag,
  Search,
  Dumbbell,
  Heart,
  Star,
  BookOpen,
  Video,
  Award,
  Flame,
  Target,
  Crown,
  Map,
  Building,
  Apple,
  HeartPulse,
  Stethoscope,
  type LucideIcon,
} from "lucide-react";

export type NavigationContextId = 
  | 'home' 
  | 'trainers' 
  | 'events' 
  | 'tribus' 
  | 'rankings' 
  | 'directory' 
  | 'content'
  | 'booking';

export interface NavMenuItem {
  path: string;
  label: string;
  icon: LucideIcon;
  hasDropdown?: boolean;
  dropdownId?: string;
  special?: boolean;
}

export interface DropdownItem {
  label: string;
  path: string;
  icon?: LucideIcon;
  description?: string;
  divider?: boolean;
}

export interface DropdownConfig {
  label: string;
  items: DropdownItem[];
}

export interface ContextMenuConfig {
  menuItems: NavMenuItem[];
  stickyItems: string[];
  cta: { label: string; path?: string; action?: 'auth' };
}

export interface NavigationContext {
  contextId: NavigationContextId;
  label: string;
  icon: LucideIcon;
  visitor: ContextMenuConfig;
  registered: ContextMenuConfig;
  dropdowns: Record<string, DropdownConfig>;
}

// Professional services dropdown - shared across contexts
export const SERVICES_DROPDOWN: DropdownConfig = {
  label: "Servicios",
  items: [
    { label: "Entrenadores", path: "/trainers", icon: Dumbbell, description: "Entrenadores personales certificados" },
    { label: "Nutricionistas", path: "/nutricionistas", icon: Apple, description: "Expertos en nutrición y dietética" },
    { label: "Fisioterapeutas", path: "/fisioterapeutas", icon: HeartPulse, description: "Rehabilitación y terapia física" },
    { label: "Sistema Reservas", path: "/sistema-reservas", icon: Calendar, description: "Gestión de citas y pagos" },
    { label: "Ver todos", path: "/servicios-profesionales", divider: true },
  ],
};

// Directory dropdown - gyms, stores, centers
export const DIRECTORY_DROPDOWN: DropdownConfig = {
  label: "Red Deportiva",
  items: [
    { label: "Gimnasios", path: "/directory?type=gimnasio", icon: Dumbbell, description: "Encuentra tu gimnasio ideal" },
    { label: "Tiendas Deportivas", path: "/tiendas", icon: ShoppingBag, description: "Equipamiento y suplementos" },
    { label: "Centros Deportivos", path: "/directory?type=centro", icon: Building, description: "Instalaciones y clubes" },
    { label: "Ver todo", path: "/directory", divider: true },
  ],
};

// Dropdown configurations shared across contexts
export const SPECIALTIES_DROPDOWN: DropdownConfig = {
  label: "Especialidades",
  items: [
    { label: "CrossFit", path: "/trainers?specialty=crossfit", icon: Flame },
    { label: "Yoga", path: "/trainers?specialty=yoga", icon: Heart },
    { label: "Personal Training", path: "/trainers?specialty=personal-training", icon: Dumbbell },
    { label: "Nutrición", path: "/trainers?specialty=nutricion", icon: Target },
    { label: "Pérdida de Peso", path: "/trainers?specialty=perdida-peso", icon: Target },
    { label: "Hipertrofia", path: "/trainers?specialty=hipertrofia", icon: Dumbbell },
    { label: "Preparación Atlética", path: "/trainers?specialty=preparacion-atletica", icon: Trophy },
    { label: "Ver todas", path: "/trainers", divider: true },
  ],
};

export const CITIES_DROPDOWN: DropdownConfig = {
  label: "Por Ciudad",
  items: [
    { label: "Madrid", path: "/trainers?city=Madrid", icon: Building },
    { label: "Barcelona", path: "/trainers?city=Barcelona", icon: Building },
    { label: "Valencia", path: "/trainers?city=Valencia", icon: Building },
    { label: "Sevilla", path: "/trainers?city=Sevilla", icon: Building },
    { label: "Bilbao", path: "/trainers?city=Bilbao", icon: Building },
    { label: "Málaga", path: "/trainers?city=Málaga", icon: Building },
    { label: "Ver todas", path: "/trainers", divider: true },
  ],
};

export const EVENT_TYPES_DROPDOWN: DropdownConfig = {
  label: "Tipo de Evento",
  items: [
    { label: "Competiciones", path: "/events?type=competicion", icon: Trophy },
    { label: "Workshops", path: "/events?type=workshop", icon: BookOpen },
    { label: "Clases Grupales", path: "/events?type=clase", icon: Users },
    { label: "Networking", path: "/events?type=networking", icon: Users },
    { label: "Ver todos", path: "/events", divider: true },
  ],
};

export const TRIBUS_SPORTS_DROPDOWN: DropdownConfig = {
  label: "Deportes",
  items: [
    { label: "Running", path: "/tribus?deporte=running", icon: Target },
    { label: "CrossFit", path: "/tribus?deporte=crossfit", icon: Flame },
    { label: "Ciclismo", path: "/tribus?deporte=ciclismo", icon: Target },
    { label: "Yoga", path: "/tribus?deporte=yoga", icon: Heart },
    { label: "Calistenia", path: "/tribus?deporte=calistenia", icon: Dumbbell },
    { label: "Ver todas", path: "/tribus", divider: true },
  ],
};

export const RANKINGS_CATEGORIES_DROPDOWN: DropdownConfig = {
  label: "Categoría",
  items: [
    { label: "Entrenadores", path: "/los-mejores/entrenadores", icon: User },
    { label: "Gimnasios", path: "/los-mejores/gimnasios", icon: Building },
    { label: "Eventos", path: "/los-mejores/eventos", icon: Calendar },
    { label: "Tribus", path: "/los-mejores/tribus", icon: Users },
    { label: "Hall of Fame", path: "/los-mejores", icon: Crown },
  ],
};

// Navigation contexts configuration
export const NAVIGATION_CONTEXTS: Record<NavigationContextId, NavigationContext> = {
  home: {
    contextId: 'home',
    label: 'Inicio',
    icon: Compass,
    visitor: {
      menuItems: [
        { path: "/trainers", label: "Profesionales", icon: Stethoscope, hasDropdown: true, dropdownId: 'services' },
        { path: "/directory", label: "Red Deportiva", icon: Building, hasDropdown: true, dropdownId: 'directory' },
        { path: "/tiendas", label: "Tiendas", icon: ShoppingBag },
        { path: "/cerca-de-mi", label: "Cerca de Mí", icon: MapPin, special: true },
        { path: "/events", label: "Eventos", icon: Calendar },
        { path: "/articles", label: "Blog", icon: BookOpen },
        { path: "/videos", label: "Videos", icon: Video },
      ],
      stickyItems: ["Profesionales", "Red Deportiva", "Cerca de Mí"],
      cta: { label: "Iniciar Sesión", action: 'auth' },
    },
    registered: {
      menuItems: [
        { path: "/trainers", label: "Profesionales", icon: Stethoscope, hasDropdown: true, dropdownId: 'services' },
        { path: "/directory", label: "Red Deportiva", icon: Building, hasDropdown: true, dropdownId: 'directory' },
        { path: "/tiendas", label: "Tiendas", icon: ShoppingBag },
        { path: "/cerca-de-mi", label: "Cerca de Mí", icon: MapPin, special: true },
        { path: "/tribus", label: "Tribus", icon: Users },
        { path: "/events", label: "Eventos", icon: Calendar },
        { path: "/los-mejores", label: "Rankings", icon: Trophy },
        { path: "/articles", label: "Blog", icon: BookOpen },
        { path: "/videos", label: "Videos", icon: Video },
      ],
      stickyItems: ["Profesionales", "Red Deportiva", "Rankings"],
      cta: { label: "Registra tu Negocio", path: "/submit-listing" },
    },
    dropdowns: {
      services: SERVICES_DROPDOWN,
      directory: DIRECTORY_DROPDOWN,
    },
  },

  trainers: {
    contextId: 'trainers',
    label: 'Entrenadores',
    icon: Dumbbell,
    visitor: {
      menuItems: [
        { path: "/trainers", label: "Buscar", icon: Search },
        { path: "/trainers", label: "Especialidades", icon: Dumbbell, hasDropdown: true, dropdownId: 'specialties' },
        { path: "/trainers", label: "Por Ciudad", icon: MapPin, hasDropdown: true, dropdownId: 'cities' },
        { path: "/trainers", label: "Otros Profesionales", icon: Stethoscope, hasDropdown: true, dropdownId: 'services' },
        { path: "/cerca-de-mi", label: "Cerca de Mí", icon: MapPin },
      ],
      stickyItems: ["Buscar", "Por Ciudad"],
      cta: { label: "Iniciar Sesión", action: 'auth' },
    },
    registered: {
      menuItems: [
        { path: "/trainers", label: "Buscar", icon: Search },
        { path: "/trainers", label: "Especialidades", icon: Dumbbell, hasDropdown: true, dropdownId: 'specialties' },
        { path: "/trainers", label: "Por Ciudad", icon: MapPin, hasDropdown: true, dropdownId: 'cities' },
        { path: "/trainers", label: "Otros Profesionales", icon: Stethoscope, hasDropdown: true, dropdownId: 'services' },
        { path: "/cerca-de-mi", label: "Cerca de Mí", icon: MapPin },
        { path: "/los-mejores", label: "Rankings", icon: Trophy },
      ],
      stickyItems: ["Buscar", "Cerca de Mí", "Rankings"],
      cta: { label: "Regístrate", path: "/become-trainer" },
    },
    dropdowns: {
      specialties: SPECIALTIES_DROPDOWN,
      cities: CITIES_DROPDOWN,
      services: SERVICES_DROPDOWN,
    },
  },

  events: {
    contextId: 'events',
    label: 'Eventos',
    icon: Calendar,
    visitor: {
      menuItems: [
        { path: "/events", label: "Calendario", icon: Calendar },
        { path: "/events", label: "Por Ciudad", icon: MapPin, hasDropdown: true, dropdownId: 'cities' },
        { path: "/events", label: "Por Tipo", icon: Star, hasDropdown: true, dropdownId: 'eventTypes' },
      ],
      stickyItems: ["Calendario", "Por Ciudad"],
      cta: { label: "Iniciar Sesión", action: 'auth' },
    },
    registered: {
      menuItems: [
        { path: "/events", label: "Calendario", icon: Calendar },
        { path: "/events", label: "Por Ciudad", icon: MapPin, hasDropdown: true, dropdownId: 'cities' },
        { path: "/events", label: "Por Tipo", icon: Star, hasDropdown: true, dropdownId: 'eventTypes' },
        { path: "/los-mejores", label: "Rankings", icon: Trophy },
        { path: "/create-event", label: "Publicar Evento", icon: Calendar },
      ],
      stickyItems: ["Calendario", "Por Ciudad", "Publicar Evento"],
      cta: { label: "Publicar Evento", path: "/create-event" },
    },
    dropdowns: {
      cities: {
        label: "Por Ciudad",
        items: [
          { label: "Madrid", path: "/events?city=Madrid", icon: Building },
          { label: "Barcelona", path: "/events?city=Barcelona", icon: Building },
          { label: "Valencia", path: "/events?city=Valencia", icon: Building },
          { label: "Ver todos", path: "/events", divider: true },
        ],
      },
      eventTypes: EVENT_TYPES_DROPDOWN,
    },
  },

  tribus: {
    contextId: 'tribus',
    label: 'Tribus',
    icon: Users,
    visitor: {
      menuItems: [
        { path: "/tribus", label: "Explorar", icon: Compass },
        { path: "/tribus", label: "Deportes", icon: Dumbbell, hasDropdown: true, dropdownId: 'sports' },
        { path: "/los-mejores", label: "Leaderboard", icon: Trophy },
      ],
      stickyItems: ["Explorar", "Deportes"],
      cta: { label: "Iniciar Sesión", action: 'auth' },
    },
    registered: {
      menuItems: [
        { path: "/tribus", label: "Explorar", icon: Compass },
        { path: "/tribus", label: "Deportes", icon: Dumbbell, hasDropdown: true, dropdownId: 'sports' },
        { path: "/tribus/mis-tribus", label: "Mis Tribus", icon: Users },
        { path: "/desafios", label: "Desafíos", icon: Target },
        { path: "/los-mejores", label: "Leaderboard", icon: Trophy },
      ],
      stickyItems: ["Explorar", "Mis Tribus", "Desafíos"],
      cta: { label: "Crear Tribu", path: "/tribus/crear" },
    },
    dropdowns: {
      sports: TRIBUS_SPORTS_DROPDOWN,
    },
  },

  rankings: {
    contextId: 'rankings',
    label: 'Rankings',
    icon: Trophy,
    visitor: {
      menuItems: [
        { path: "/los-mejores", label: "Hall of Fame", icon: Crown },
        { path: "/los-mejores", label: "Categoría", icon: Trophy, hasDropdown: true, dropdownId: 'categories' },
        { path: "/los-mejores", label: "Por País", icon: Map },
      ],
      stickyItems: ["Hall of Fame", "Categoría"],
      cta: { label: "Registrarse para Votar", action: 'auth' },
    },
    registered: {
      menuItems: [
        { path: "/los-mejores", label: "Hall of Fame", icon: Crown },
        { path: "/los-mejores", label: "Categoría", icon: Trophy, hasDropdown: true, dropdownId: 'categories' },
        { path: "/los-mejores", label: "Por País", icon: Map },
        { path: "/los-mejores/votar", label: "Votar", icon: Award },
      ],
      stickyItems: ["Hall of Fame", "Categoría", "Votar"],
      cta: { label: "Votar Ahora", path: "/los-mejores/votar" },
    },
    dropdowns: {
      categories: RANKINGS_CATEGORIES_DROPDOWN,
    },
  },

  directory: {
    contextId: 'directory',
    label: 'Red Deportiva',
    icon: Compass,
    visitor: {
      menuItems: [
        { path: "/directory", label: "Explorar", icon: Compass },
        { path: "/directory?type=gimnasio", label: "Gimnasios", icon: Dumbbell },
        { path: "/tiendas", label: "Tiendas", icon: ShoppingBag },
        { path: "/directory?type=centro", label: "Centros", icon: Building },
        { path: "/trainers", label: "Profesionales", icon: Stethoscope, hasDropdown: true, dropdownId: 'services' },
        { path: "/cerca-de-mi", label: "Mapa", icon: Map },
      ],
      stickyItems: ["Explorar", "Gimnasios", "Tiendas"],
      cta: { label: "Iniciar Sesión", action: 'auth' },
    },
    registered: {
      menuItems: [
        { path: "/directory", label: "Explorar", icon: Compass },
        { path: "/directory?type=gimnasio", label: "Gimnasios", icon: Dumbbell },
        { path: "/tiendas", label: "Tiendas", icon: ShoppingBag },
        { path: "/directory?type=centro", label: "Centros", icon: Building },
        { path: "/trainers", label: "Profesionales", icon: Stethoscope, hasDropdown: true, dropdownId: 'services' },
        { path: "/cerca-de-mi", label: "Mapa", icon: Map },
        { path: "/dashboard/favorites", label: "Mis Favoritos", icon: Heart },
      ],
      stickyItems: ["Explorar", "Gimnasios", "Tiendas", "Mapa"],
      cta: { label: "Registra tu Negocio", path: "/submit-listing" },
    },
    dropdowns: {
      services: SERVICES_DROPDOWN,
    },
  },

  content: {
    contextId: 'content',
    label: 'Contenido',
    icon: BookOpen,
    visitor: {
      menuItems: [
        { path: "/articles", label: "Blog", icon: BookOpen },
        { path: "/videos", label: "Videos", icon: Video },
        { path: "/articles?category=guias", label: "Guías", icon: BookOpen },
      ],
      stickyItems: ["Blog", "Videos"],
      cta: { label: "Iniciar Sesión", action: 'auth' },
    },
    registered: {
      menuItems: [
        { path: "/articles", label: "Blog", icon: BookOpen },
        { path: "/videos", label: "Videos", icon: Video },
        { path: "/articles?category=guias", label: "Guías", icon: BookOpen },
        { path: "/articles?category=tips", label: "Tips", icon: Star },
      ],
      stickyItems: ["Blog", "Videos", "Guías"],
      cta: { label: "Contribuir", path: "/submit-article" },
    },
    dropdowns: {},
  },

  booking: {
    contextId: 'booking',
    label: 'Sistema de Reservas',
    icon: Calendar,
    visitor: {
      menuItems: [
        { path: "/sistema-reservas", label: "Información", icon: Star },
        { path: "/trainers", label: "Entrenadores", icon: Dumbbell },
        { path: "/nutricionistas", label: "Nutricionistas", icon: Apple },
        { path: "/fisioterapeutas", label: "Fisioterapeutas", icon: HeartPulse },
      ],
      stickyItems: ["Información", "Entrenadores"],
      cta: { label: "Iniciar Sesión", action: 'auth' },
    },
    registered: {
      menuItems: [
        { path: "/sistema-reservas", label: "Información", icon: Star },
        { path: "/dashboard", label: "Mi Dashboard", icon: Building },
        { path: "/trainers", label: "Entrenadores", icon: Dumbbell },
        { path: "/nutricionistas", label: "Nutricionistas", icon: Apple },
      ],
      stickyItems: ["Mi Dashboard", "Información"],
      cta: { label: "Gestionar Reservas", path: "/dashboard" },
    },
    dropdowns: {
      services: SERVICES_DROPDOWN,
    },
  },
};

// Route pattern matching for context detection
export const ROUTE_PATTERNS: { patterns: string[]; contextId: NavigationContextId }[] = [
  { patterns: ['/trainers', '/trainer/', '/entrenador/', '/become-trainer', '/edit-trainer-profile', '/nutricionistas', '/fisioterapeutas'], contextId: 'trainers' },
  { patterns: ['/events', '/event/', '/eventos', '/create-event'], contextId: 'events' },
  { patterns: ['/tribus', '/desafios'], contextId: 'tribus' },
  { patterns: ['/los-mejores'], contextId: 'rankings' },
  { patterns: ['/directory', '/listing/', '/directorio', '/explore', '/tiendas-proveedores', '/store', '/stores', '/tiendas', '/tienda/'], contextId: 'directory' },
  { patterns: ['/articles', '/article/', '/videos', '/video/'], contextId: 'content' },
  { patterns: ['/sistema-reservas', '/dashboard/reservas', '/dashboard/servicios', '/dashboard/disponibilidad'], contextId: 'booking' },
];
